import { useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Dialog } from "@tellescope/design-system";
import type { StripeSubscription } from "../../../hooks/useStripeSubscriptions";
import type { PauseIframeConfig } from "../../../../shared/schema";
import { useIframeToken } from "../../../hooks/useIframeToken";
import { buildUrlWithParams, buildSandboxAttr, getHeightStyle, DEFAULT_IFRAME_SANDBOX } from "../iframeUtils";

interface PauseIframeDialogProps {
  subscription: StripeSubscription;
  config: PauseIframeConfig;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  onNavigateToCancel?: () => void;
}

export const PauseIframeDialog = ({
  subscription,
  config,
  open,
  onOpenChange,
  onSuccess,
  onNavigateToCancel,
}: PauseIframeDialogProps) => {
  const { t } = useTranslation("payments");
  const { token, isLoading, error } = useIframeToken({ enabled: open && (config.passToken ?? true) });

  // Build URL with token and subscription ID
  const finalUrl = useMemo(() => {
    if (!config.url) return null;

    const params: Record<string, string> = {
      [config.subscriptionIdParam ?? "subscriptionId"]: subscription.id,
    };

    if ((config.passToken ?? true) && token) {
      params[config.tokenParam ?? "token"] = token;
    }

    if ((config.passToken ?? true) && !token) return null; // Still loading token

    return buildUrlWithParams(config.url, params);
  }, [config, subscription.id, token]);

  // Listen for success/close messages from iframe
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (!config.url) return;
      try {
        const expectedOrigin = new URL(config.url).origin;
        if (event.origin !== expectedOrigin) return;
      } catch {
        return;
      }

      if (event.data?.type === "subscription-paused") {
        onSuccess?.();
        onOpenChange(false);
      } else if (event.data?.type === "close-dialog") {
        onOpenChange(false);
      } else if (event.data?.type === "navigate-to-cancel") {
        onOpenChange(false);
        onNavigateToCancel?.();
      }
    },
    [config.url, onOpenChange, onSuccess, onNavigateToCancel]
  );

  useEffect(() => {
    if (!open) return;
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [open, handleMessage]);

  const sandboxAttr = buildSandboxAttr(DEFAULT_IFRAME_SANDBOX);
  const heightStyle = getHeightStyle(config.height ?? "md");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="sm:max-w-2xl p-0 gap-0 overflow-hidden">
        <Dialog.Header className="p-6 pb-4">
          <Dialog.Title>{config.title ?? t("Pause Subscription")}</Dialog.Title>
        </Dialog.Header>

        {isLoading && (
          <div
            className="flex min-h-0 items-center justify-center bg-muted/30"
            style={{ height: heightStyle }}
            data-testid="pause-iframe-loader"
          >
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <div className="flex min-h-0 items-center justify-center bg-muted/30 p-4" style={{ height: heightStyle }}>
            <p className="text-destructive">{t("Failed to load. Please try again.")}</p>
          </div>
        )}

        {finalUrl && !isLoading && !error && (
          <iframe
            src={finalUrl}
            title={config.title ?? t("Pause Subscription")}
            sandbox={sandboxAttr}
            className="min-h-0 w-full border-0 border-t"
            style={{ height: heightStyle }}
          />
        )}
      </Dialog.Content>
    </Dialog>
  );
};
